/**
 * Local Storage management helpers for demo sessions.
 *
 * Provides type-safe access to demo session data stored in localStorage.
 */

const DEMO_TOKEN_KEY = 'demo_token'
const DEMO_EXPIRES_KEY = 'demo_expires_at'
const DEMO_SCENARIO_KEY = 'demo_scenario'
const TOUR_ACTIVE_KEY = 'demo_tour_active'

export interface DemoSession {
  token: string
  expiresAt: Date
  scenario?: string
  tourActive?: boolean
}

/**
 * Get the current demo session from localStorage.
 *
 * @returns Demo session data or null if not found
 */
export function getDemoSession(): DemoSession | null {
  const token = localStorage.getItem(DEMO_TOKEN_KEY)
  const expiresAtStr = localStorage.getItem(DEMO_EXPIRES_KEY)

  if (!token || !expiresAtStr) {
    return null
  }

  try {
    const expiresAt = new Date(expiresAtStr)
    const scenario = localStorage.getItem(DEMO_SCENARIO_KEY) || undefined
    const tourActive = localStorage.getItem(TOUR_ACTIVE_KEY) === 'true'

    return {
      token,
      expiresAt,
      scenario,
      tourActive,
    }
  } catch (error) {
    console.error('Failed to parse demo session:', error)
    return null
  }
}

/**
 * Store a demo session in localStorage.
 *
 * @param session - Demo session data to store
 */
export function setDemoSession(session: DemoSession): void {
  localStorage.setItem(DEMO_TOKEN_KEY, session.token)
  localStorage.setItem(DEMO_EXPIRES_KEY, session.expiresAt.toISOString())

  if (session.scenario) {
    localStorage.setItem(DEMO_SCENARIO_KEY, session.scenario)
  }

  if (session.tourActive !== undefined) {
    localStorage.setItem(TOUR_ACTIVE_KEY, session.tourActive.toString())
  }
}

/**
 * Clear all demo session data from localStorage.
 */
export function clearDemoSession(): void {
  localStorage.removeItem(DEMO_TOKEN_KEY)
  localStorage.removeItem(DEMO_EXPIRES_KEY)
  localStorage.removeItem(DEMO_SCENARIO_KEY)
  localStorage.removeItem(TOUR_ACTIVE_KEY)
}

/**
 * Check if the demo session is still valid (not expired).
 *
 * @returns True if session is valid, false otherwise
 */
export function isDemoSessionValid(): boolean {
  const session = getDemoSession()

  if (!session) {
    return false
  }

  const now = new Date()
  return session.expiresAt > now
}

/**
 * Get the time remaining in the demo session.
 *
 * @returns Hours remaining or null if no session
 */
export function getDemoTimeRemaining(): number | null {
  const session = getDemoSession()

  if (!session) {
    return null
  }

  const now = new Date()
  const diffMs = session.expiresAt.getTime() - now.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  return Math.max(0, diffHours)
}

/**
 * Check if the demo session is expiring soon (less than 1 hour).
 *
 * @returns True if expiring soon, false otherwise
 */
export function isDemoSessionExpiringSoon(): boolean {
  const remaining = getDemoTimeRemaining()
  return remaining !== null && remaining < 1
}

/**
 * Get formatted time remaining string.
 *
 * @returns Formatted string like "2h 30m" or "45m" or null if no session
 */
export function getFormattedTimeRemaining(): string | null {
  const remaining = getDemoTimeRemaining()

  if (remaining === null) {
    return null
  }

  if (remaining < 1) {
    const minutes = Math.floor(remaining * 60)
    return `${minutes}m`
  }

  const hours = Math.floor(remaining)
  const minutes = Math.floor((remaining - hours) * 60)

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
}
